import BasePlayer from 'services/sports/default/base-player';
import Action from 'actions';
import Const from 'constants';
import {
    calcRangeOfOutcomesValue,
    getRandomisationByPositionsValuePtc,
} from 'components/organisms/range-of-outcomes/helpers';
import NumberHelper from 'helpers/number-helper';
import {addRoleSuffix} from 'helpers/simplex-helper';
import {calcSympathyPoints, getDefaultExposure} from 'helpers/player-helper';
import {calcUserScore} from "helpers/common-helper";

export default class Player extends BasePlayer {
    resetSettings({incomingValues: {dispatch}}) {
        const {
            playersData: {players, games},
            userPointsType,
        } = Action.getState();

        const playersUserPoints = [];
        const playersExposure = [];

        players.getAllAsArr().map((player) => {
            if (!games.isPlayerGameInactive(player)) {
                playersUserPoints.push({
                    playerId: player.playerId,
                    userPoints: this.exec('applySympathyToPoints', {
                        player,
                        points: player[userPointsType.getUserPointsType()],
                    }),
                });
            }

            playersExposure.push({
                playerId: player.playerId,
                exposureMin: getDefaultExposure('min'),
                exposureMax: getDefaultExposure('max'),
            });
        });

        dispatch(Action.do('resetExcludePlayersByRange'));
        dispatch(Action.set('PlayersExposure', playersExposure));
        dispatch(Action.set('UserPoints', playersUserPoints, Const.FLEX_MODE, false, {countPlayers: false}));

        return true;
    }

    resetSettings_ShowDown({incomingValues: {dispatch, modes = [Const.CAPTAIN_MODE, Const.FLEX_MODE]}}) {
        const {
            playersData: {players, games},
            userPointsType,
        } = Action.getState();

        const userPointsByModes = {};
        players.getAllAsArr().map((player) => {
            if (games.isPlayerGameInactive(player)) {
                return;
            }
            modes.map((mode) => {
                userPointsByModes[mode] ??= [];
                userPointsByModes[mode].push({
                    playerId: player.getId(),
                    userPoints: this.exec('applySympathyToPoints', {
                        player,
                        points: player.getPropValueOfMode(userPointsType.getUserPointsType(), mode),
                        mode,
                    }),
                });
            });
        });

        if (isEmpty(userPointsByModes)) {
            return true;
        }

        modes.map((mode) => {
            dispatch(Action.set('UserPoints', userPointsByModes[mode], mode, false));
        });

        return true;
    }

    prepareRandomPoints() {
        const {rangeOfOutcomesByPositions, sportConfig, lineupsQuantity, playersData, projectionPointsType} =
            Action.getState();
        const items = getRandomisationByPositionsValuePtc(rangeOfOutcomesByPositions, sportConfig, lineupsQuantity);

        if (isEmpty(items)) {
            return {};
        }

        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();
        const propName = projectionPointsType.getProjectionPointsType();

        const playerPoints = {};
        Object.values(players).map((player) => {
            for (let item of items) {
                const duplicates = arrayDuplicates(player.getPositionNames(), item.positions);

                if (isEmpty(duplicates)) {
                    continue;
                }

                const points = NumberHelper.float(player[propName], 3);

                playerPoints[player.getId()] = calcRangeOfOutcomesValue(points, item.value);

                break;
            }
        });

        return {...playerPoints};
    }

    prepareRandomPointsByModes({incomingValues = {}}) {
        const {rangeOfOutcomesByPositions, sportConfig, lineupsQuantity, playersData, projectionPointsType} =
            Action.getState();
        const items = getRandomisationByPositionsValuePtc(rangeOfOutcomesByPositions, sportConfig, lineupsQuantity);

        if (isEmpty(items)) {
            return {};
        }

        const {preparedPlayers} = playersData;
        const players = preparedPlayers.groupedBy.playerId();
        const {modes = [Const.CAPTAIN_MODE, Const.DEFAULT_MODE]} = incomingValues;
        const prop = projectionPointsType.getProjectionPointsType();

        const projPointsTypesIndexedByMode = {};
        modes.map((mode) => {
            projPointsTypesIndexedByMode[mode] = this.exec('definePropNameOfMode', {prop, mode});
        });

        const playerPoints = {};
        Object.values(players).map((player) => {
            for (let item of items) {
                const duplicates = arrayDuplicates(player.getPositionNames(), item.positions);

                if (!isEmpty(duplicates)) {
                    continue;
                }

                Object.keys(projPointsTypesIndexedByMode).map((mode) => {
                    const propName = projPointsTypesIndexedByMode[mode];
                    const points = NumberHelper.float(player[propName], 3);
                    const key = addRoleSuffix({prefix: player.getId(), role: mode});

                    playerPoints[key] = calcRangeOfOutcomesValue(points, item.value);
                });

                break;
            }
        });

        return {...playerPoints};
    }

    updatePlayersWhoseGamesBecameInactive({incomingValues: {dispatch, players}}) {
        if (isEmpty(players)) {
            return;
        }

        const lock = {};
        const sympathy = {};
        const exposure = [];
        const points = [];
        const chaining = [];
        const exposureMin = getDefaultExposure('min');
        const exposureMax = getDefaultExposure('max');

        players.map((player) => {
            lock[player.getId()] = false;
            sympathy[player.getId()] = null;
            exposure.push({playerId: player.getId(), exposureMin, exposureMax, mode: Const.FLEX_MODE});
            points.push({playerId: player.getId(), userPoints: 0});
            chaining.push(player.getId());
        });

        dispatch(Action.set('PlayersExposure', exposure));
        dispatch(Action.set('LockedPlayers', {[Const.FLEX_MODE]: lock}, {countPlayers: false}));
        dispatch(Action.set('PlayersSympathy', {[Const.FLEX_MODE]: sympathy}));
        dispatch(Action.set('UserPoints', points, Const.FLEX_MODE, false, {countPlayers: false}));
        dispatch(
            Action.set(
                'UsageOfPlayersOnlyInTeamStacking',
                {playerIds: chaining, isChecked: false},
                {countPlayers: false},
            ),
        );

        dispatch(Action.setPlayersCounters());
    }

    updatePlayersWhoseGamesBecameActive({incomingValues: {dispatch, players}}) {
        if (isEmpty(players)) {
            return;
        }

        const points = players.map((player) => {
            return {
                playerId: player.getId(),
                userPoints: player.adminPoints,
            };
        });

        dispatch(Action.set('UserPoints', points, Const.FLEX_MODE, false, {countPlayers: false}));

        dispatch(Action.setPlayersCounters());
    }

    updatePlayersWhoseGamesBecameInactive_ShowDown({incomingValues: {dispatch, players, modes}}) {
        if (isEmpty(players)) {
            return;
        }

        const exposure = [];
        const lockByModes = {};
        const sympathyByModes = {};
        const pointsByModes = {};
        const exposureMin = getDefaultExposure('min');
        const exposureMax = getDefaultExposure('max');

        players.map((player) => {
            exposure.push({
                playerId: player.getId(),
                exposureMax,
                mode: Const.OVERALL_MODE,
            });

            modes.map((mode) => {
                pointsByModes[mode] ??= [];
                pointsByModes[mode].push({
                    playerId: player.getId(),
                    userPoints: 0,
                });

                exposure.push({
                    playerId: player.getId(),
                    exposureMin,
                    exposureMax,
                    mode,
                });

                lockByModes[mode] ??= {};
                lockByModes[mode][player.getId()] = false;

                sympathyByModes[mode] ??= {};
                sympathyByModes[mode][player.getId()] = null;
            });
        });

        modes.map((mode) => {
            dispatch(Action.set('UserPoints', pointsByModes[mode], mode, false, {countPlayers: false}));
        });

        dispatch(Action.set('LockedPlayers', lockByModes, {countPlayers: false}));
        dispatch(Action.set('PlayersExposure', exposure));
        dispatch(Action.set('PlayersSympathy', sympathyByModes));

        dispatch(Action.setPlayersCounters());
    }

    updatePlayersWhoseGamesBecameActive_ShowDown({incomingValues: {dispatch, players, modes}}) {
        if (isEmpty(players)) {
            return;
        }

        const pointsByModes = {};

        players.map((player) => {
            modes.map((mode) => {
                pointsByModes[mode] ??= [];
                pointsByModes[mode].push({
                    playerId: player.getId(),
                    userPoints: player.getPropValueOfMode('adminPoints', mode),
                });
            });
        });

        modes.map((mode) => {
            dispatch(Action.set('UserPoints', pointsByModes[mode], mode, false, {countPlayers: false}));
        });

        dispatch(Action.setPlayersCounters());
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        const {
            playersData: {players, games},
            playersSympathy,
        } = Action.getState();

        const result = {};

        Object.keys(indexedPlayers).map((key) => {
            const newPlayer = indexedPlayers[key];
            const player = {...players.getPlayer(newPlayer.getId())};

            if (isEmpty(player)) {
                result[key] = {...newPlayer};
                return;
            }

            let userPoints = player.userPoints;
            let userScore = player.userScore;

            if (!player.pointsChangedManually && !games.isPlayerGameInactive(player)) {
                const sympathy = playersSympathy.getByPlayerIdAndMode(player.getId());
                userPoints = NumberHelper.float(
                    calcSympathyPoints(newPlayer.userPoints, Const.SYMPATHIES_LIST[sympathy]),
                    3,
                );
                userScore = calcUserScore(userPoints, newPlayer.salary, newPlayer.userScoreMultiplier);
            }

            let positionName = newPlayer.positionName;
            let position = newPlayer.position;

            const isPositionPresent =
                newPlayer.hasOwnProperty('selectablePositionsMap') &&
                newPlayer.selectablePositionsMap.hasOwnProperty(player.positionName);

            if (isPositionPresent) {
                positionName = player.positionName;
                position = player.position;
            }

            const {projOwn = {}, ...newPlayerProps} = newPlayer;

            if ('projOwn' in player) {
                player.projOwn.setValueFromPlayerPayload(newPlayer);
            }

            result[key] = {
                ...player,
                ...newPlayerProps,
                pointsChangedManually: player.pointsChangedManually,
                userPoints,
                userScore,
                grades: newPlayer.grades,
                exposureMax: player.exposureMax,
                exposureMin: player.exposureMin,
                positionName,
                position,
            };
        });

        return {...result};
    }
}
